import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input, Autocomplete } from '~/components/Form';
import { GERENCIA_ABASTECIMENTO } from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import {
  storeCentroCusto,
  updateCentroCusto,
} from '~/store/modules/centrocusto/actions';

export default function CreateOrUpdate({ reference, create, data }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.centrocusto);

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idcentrocusto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomecentrocusto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        idgerencia: can(GERENCIA_ABASTECIMENTO)
          ? Yup.string()
              .nullable()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                (value) => validCharacters(value)
              )
          : Yup.string().nullable(),
        statuscentrocusto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeCentroCusto(objSend, params));
      } else {
        await dispatch(updateCentroCusto(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idcentrocusto"
            label={t('s.cadastro.centrocusto.idcentrocusto')}
            variant="outlined"
            fullWidth
            disabled={!create}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="nomecentrocusto"
            label={t('s.cadastro.centrocusto.nomecentrocusto')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          {can(GERENCIA_ABASTECIMENTO) && (
            <Autocomplete
              name="idgerencia"
              label="nomegerencia"
              url="gerencias"
              unique="idgerencia"
              title={t('s.cadastro.centrocusto.nomegerencia')}
            />
          )}

          <Select
            name="statuscentrocusto"
            variant="outlined"
            size="small"
            label={t('s.cadastro.centrocusto.statuscentrocusto')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
